import { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

interface TourStep {
  target: string; // CSS selector
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="sidebar"]',
    title: 'Navigation Sidebar',
    description: 'Access all Spaciora modules from the sidebar. Click any module to navigate.',
    position: 'right',
  },
  {
    target: '[data-tour="search"]',
    title: 'Global Search',
    description: 'Press Cmd+K to search across tickets, assets, visitors, and more.',
    position: 'bottom',
  },
  {
    target: '[data-tour="notifications"]',
    title: 'Notification Centre',
    description: 'Click the bell to see real-time notifications about tickets, bookings, and alerts.',
    position: 'bottom',
  },
  {
    target: '[data-tour="quick-actions"]',
    title: 'Quick Actions',
    description: 'Press Q to open quick actions. Book desks, raise tickets, and pre-register visitors instantly.',
    position: 'bottom',
  },
  {
    target: '[data-tour="dashboard-kpis"]',
    title: 'Dashboard KPIs',
    description: 'Your key metrics at a glance. Click any card to drill down into the details.',
    position: 'bottom',
  },
  {
    target: '[data-tour="user-menu"]',
    title: 'Your Profile',
    description: 'Access settings, preferences, and sign out from your profile menu.',
    position: 'left',
  },
];

const TOUR_STORAGE_KEY = 'spaciora-tour-completed';

/** Below the lg breakpoint the sidebar is a drawer and the header is all the
 *  screen has at the top. */
const PHONE_MAX_WIDTH = 1023;
const isPhone = () => typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH;

/** A step whose target is on screen. At 390px the sidebar is hidden, so step 1
 *  ("Navigation Sidebar") pointed at nothing and its card sat over the header
 *  (Employee B13). A target with no box is not shown, so its step is skipped. */
function targetIsVisible(selector: string): boolean {
  const el = document.querySelector(selector);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

export default function OnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  // The steps this screen can actually show, worked out when the tour opens.
  // On a desktop every target is there and this is all six, as before.
  const [steps, setSteps] = useState<TourStep[]>(TOUR_STEPS);
  const [phone, setPhone] = useState(false);

  // Show tour only on first visit
  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!completed) {
      const timer = setTimeout(() => {
        const onPhone = isPhone();
        setPhone(onPhone);
        // Only on a phone are hidden targets dropped: a desktop keeps every
        // step exactly as it was, including one whose target is still loading.
        setSteps(onPhone ? TOUR_STEPS.filter((st) => targetIsVisible(st.target)) : TOUR_STEPS);
        setIsActive(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Update spotlight position
  useEffect(() => {
    // currentStep is held inside TOUR_STEPS by handleNext/handlePrev; the
    // check only tells the compiler so.
    const step = steps[currentStep];
    if (!isActive || !step) return;
    const el = document.querySelector(step.target);
    if (el) {
      setSpotlightRect(el.getBoundingClientRect());
    } else {
      setSpotlightRect(null);
    }
  }, [isActive, currentStep, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsActive(false);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  };

  const handleSkip = () => {
    handleComplete();
  };

  const step = steps[currentStep];
  if (!isActive || !step) return null;

  const isLast = currentStep === steps.length - 1;

  // Calculate tooltip position
  const tooltipStyle: React.CSSProperties = {};
  if (phone) {
    // 🔴 ON A PHONE THE CARD SITS AT THE FOOT OF THE SCREEN, above the bottom
    // bar, full width less the gutter — never over the header it describes.
    // Placed beside its target it covered the greeting and ran off a 390px
    // screen.
    tooltipStyle.position = 'fixed';
    tooltipStyle.left = 16;
    tooltipStyle.right = 16;
    tooltipStyle.bottom = 88;
  } else if (spotlightRect) {
    const padding = 16;
    switch (step.position) {
      case 'bottom':
        tooltipStyle.top = spotlightRect.bottom + padding;
        tooltipStyle.left = spotlightRect.left;
        break;
      case 'top':
        tooltipStyle.bottom = window.innerHeight - spotlightRect.top + padding;
        tooltipStyle.left = spotlightRect.left;
        break;
      case 'right':
        tooltipStyle.top = spotlightRect.top;
        tooltipStyle.left = spotlightRect.right + padding;
        break;
      case 'left':
        tooltipStyle.top = spotlightRect.top;
        tooltipStyle.right = window.innerWidth - spotlightRect.left + padding;
        break;
    }
  }

  return (
    // pointer-events-none on the wrapper so the tour never blocks underlying UI.
    // Children that need to receive clicks (tooltip, skip pill, spotlight ring)
    // re-enable pointer-events with pointer-events-auto.
    <div className="fixed inset-0 z-[100] pointer-events-none" aria-live="polite">
      {/* Spotlight ring — purely decorative, no pointer events */}
      {spotlightRect && (
        <div
          className="absolute rounded-xl ring-4 ring-brand-400 ring-offset-4 ring-offset-transparent bg-transparent z-[101] pointer-events-none"
          style={{
            top: spotlightRect.top - 4,
            left: spotlightRect.left - 4,
            width: spotlightRect.width + 8,
            height: spotlightRect.height + 8,
          }}
        />
      )}

      {/* Always-visible Skip pill — top-right, can't be missed */}
      {/* Not on a phone: there it sat on the header's bell and avatar, and the
          card at the foot of the screen carries its own Skip. */}
      {!phone && <button
        onClick={handleSkip}
        className="pointer-events-auto fixed right-4 top-4 z-[103] flex items-center gap-1.5 rounded-full bg-surface-inverse px-4 py-2 text-xs font-semibold text-content-inverse shadow-lg hover:bg-surface-inverse-raised"
        aria-label="Skip onboarding tour"
      >
        <XMarkIcon className="h-4 w-4" /> Skip tour
      </button>}

      {/* Tooltip */}
      <div
        data-testid="tour-card"
        className={`pointer-events-auto absolute z-[102] ${phone ? '' : 'w-80 '}rounded-xl bg-surface p-5 shadow-2xl ring-1 ring-line`}
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-700">
              Step {currentStep + 1} of {steps.length}
            </span>
            <h3 className="text-base font-semibold text-content mt-0.5">{step.title}</h3>
          </div>
          <button onClick={handleSkip} className="rounded-lg p-1 text-content-subtle hover:bg-surface-muted" aria-label="Close">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-content-muted mb-4">{step.description}</p>

        {/* Progress bar */}
        <div className="mb-4 h-1 rounded-full bg-surface-muted">
          <div
            className="h-1 rounded-full bg-brand-700 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-xs text-content-muted hover:text-content-secondary underline-offset-2 hover:underline"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-content-tertiary hover:bg-surface-subtle"
              >
                <ArrowLeftIcon className="h-3 w-3" /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1 rounded-lg bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
             aria-label="Go forward">
              {isLast ? 'Finish' : 'Next'} <ArrowRightIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
